import CachedIcon from "@mui/icons-material/CachedRounded";
import ExportIcon from "@mui/icons-material/OutputRounded";
import DownloadIcon from "@mui/icons-material/SaveAltRounded";
import { Alert, Box, Button, Container, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiResponse, JobProgress, JobStartResult, ListWithTotal } from "@entrilia/shared/api/types";
import AsyncJobProgress from "@entrilia/shared/components/AsyncJobProgress";
import DateRangeSelect from "@entrilia/shared/components/DateRangeSelect";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import { useGridDataSourceWithRange } from "@entrilia/shared/components/grid/gridHooks";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import useJobProgress from "@entrilia/shared/hooks/useJobProgress";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { scrollGridToTop } from "@entrilia/shared/utilities/dataGridApiHelper";
import type { DescriptiveDateRange } from "@entrilia/shared/utilities/dateFilterHelper";
import { lastSevenDays, today, yesterday } from "@entrilia/shared/utilities/dateFilterHelper";
import type { ExportEmailsRequest, SearchSentEmailsRequest, SentEmail } from "../../../api/types/emailsTypes";
import GeneralPageHeader from "../../common/GeneralPageHeader";
import EmailDeliveryLogGrid from "./EmailDeliveryLogGrid";
import {
  clearFiltersAction,
  emailTrackingStatusFilterMap,
  getInitialEmailDeliveryLogState,
  searchItemsAction,
  updateDatesFilterAction,
  updateStatusFilterAction,
} from "./emailDeliveryLogState";
import StatusSelect from "./filters/StatusSelect";

interface Props {
  searchApi: (payload: SearchSentEmailsRequest) => Promise<ApiResponse<ListWithTotal<SentEmail>>>;
  startExportApi: (payload: ExportEmailsRequest) => Promise<ApiResponse<JobStartResult>>;
  getExportProgressApi: (operationId: string) => Promise<ApiResponse<JobProgress>>;
}

const EmailDeliveryLog = ({ searchApi, startExportApi, getExportProgressApi }: Props) => {
  const [state, setState] = useState(getInitialEmailDeliveryLogState);
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [fileDownloadUrl, setFileDownloadUrl] = useState<string>();
  const [exportErrorMessage, setExportErrorMessage] = useState<string>();

  const handleExportJobFinished = useCallback((finishedJobProgress: JobProgress) => {
    setExporting(false);

    if (!finishedJobProgress.isSuccess) {
      setExportErrorMessage(finishedJobProgress.errorMessage ?? "Failed to export emails.");
      return;
    }

    const downloadUrl = finishedJobProgress.properties["DownloadUrl"];
    if (!downloadUrl) {
      setExportErrorMessage("The export finished but returned no download link. Please check the email we sent you.");
      return;
    }

    setFileDownloadUrl(downloadUrl);
  }, []);

  const handleExportPollingError = useCallback(() => {
    setExporting(false);
    setExportErrorMessage(
      "We could not check the export progress, but it is still running. We will email you a download link when it is ready."
    );
  }, []);

  const { jobProgress, startJobProgressCheck } = useJobProgress(getExportProgressApi, {
    onJobFinished: handleExportJobFinished,
    onPollingError: handleExportPollingError,
    updateIntervalMs: 1000,
  });

  const { gridApiRef, dataSource, totalRows, isLoading, isRefreshing, refreshDataSource } = useGridDataSourceWithRange(
    searchApi,
    state.filterParams
  );

  const handleSearch = (value: string) => {
    const searchTerm = value.trim();
    if (searchTerm.length === 0 || searchTerm.length > 1) {
      scrollGridToTop(gridApiRef);
      setState(searchItemsAction(searchTerm));
    }
  };

  const handleStatusChange = (filterValue: string) => {
    scrollGridToTop(gridApiRef);
    setState(updateStatusFilterAction(filterValue));
  };

  const handleDatesChange = (dateRange: DescriptiveDateRange) => {
    scrollGridToTop(gridApiRef);
    setState(updateDatesFilterAction(dateRange));
  };

  const showClearFilters = state.dateRange !== undefined || Boolean(state.statusFilter);

  const handleClearFilters = () => {
    scrollGridToTop(gridApiRef);
    setState(clearFiltersAction());
  };

  const handleDownloadFileClick = async () => {
    if (!fileDownloadUrl) {
      return;
    }

    setDownloading(true);
    await downloadFileFromUrl(fileDownloadUrl);
    setDownloading(false);
  };

  const handleExportToCsvClick = async () => {
    setExporting(true);
    setFileDownloadUrl(undefined);
    setExportErrorMessage(undefined);

    const [jobStartResult, jobStartError] = await withErrorHandling(startExportApi)({
      sentAtFrom: state.dateRange?.from?.toISOString(),
      sentAtTo: state.dateRange?.to?.toISOString(),
      query: state.searchTerm,
      statuses: emailTrackingStatusFilterMap[state.statusFilter],
    });

    if (jobStartError) {
      logError(jobStartError, "[EmailDeliveryLog] startSentEmailsExport");
      setExportErrorMessage(jobStartError.message || "Failed to export emails.");
      setExporting(false);
      return;
    }

    startJobProgressCheck(jobStartResult.operationId);
  };

  return (
    <>
      <GeneralPageHeader title="Email Delivery Log" />
      <Container maxWidth={false} sx={{ height: "100%", pt: 2.5, pb: 5 }}>
        <Stack spacing={1} sx={{ width: "100%", height: "100%" }}>
          <Stack spacing={2} direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
            <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
              <RecordCounter records={totalRows ?? 0} hide={isLoading} />
              <DateRangeSelect
                predefinedRanges={[today, yesterday, lastSevenDays]}
                dateRange={state.dateRange}
                onChange={handleDatesChange}
              />
              <StatusSelect filterValue={state.statusFilter} onChange={handleStatusChange} />
              {showClearFilters && (
                <Button sx={{ whiteSpace: "nowrap" }} variant="text" color="primary" onClick={handleClearFilters}>
                  Clear all
                </Button>
              )}
            </Stack>

            <Stack spacing={2} direction="row" sx={{ alignItems: "center" }}>
              <SearchField onSearch={handleSearch} debounceTimeMs={300} />
              <Button
                color="secondary"
                variant="outlined"
                startIcon={<CachedIcon />}
                onClick={refreshDataSource}
                disabled={isLoading}
                loading={isRefreshing}
              >
                Refresh
              </Button>
              <Button
                loading={exporting}
                color="secondary"
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={handleExportToCsvClick}
              >
                Export to CSV
              </Button>
            </Stack>
          </Stack>

          {jobProgress && (
            <Box sx={{ minHeight: "6rem" }}>
              <AsyncJobProgress title="Exporting emails…" jobProgress={jobProgress} />
            </Box>
          )}

          {exportErrorMessage && <Alert severity="error">{exportErrorMessage}</Alert>}

          {fileDownloadUrl && (
            <Alert
              severity="success"
              action={
                <Button
                  color="inherit"
                  loading={downloading}
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadFileClick}
                >
                  Download
                </Button>
              }
            >
              Your export is ready. We also emailed you a download link.
            </Alert>
          )}

          <EmailDeliveryLogGrid apiRef={gridApiRef} dataSource={dataSource} />
        </Stack>
      </Container>
    </>
  );
};

export default EmailDeliveryLog;
